import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";

// The quiz on a record revision. Phase 1 is authoring: what a taker sees, and how an attempt is
// scored, is the next phase and a different endpoint -- this one returns the answer key.
//
// THE PERSONA IS NOT PASSED HERE. The axios instance puts visibility-mode / visibility-value on every
// request, so a caller cannot forget them; a quiz is reached through the record it hangs off and is
// scoped by that record's access.

export type QuestionType = "SINGLE_SELECT" | "MULTI_SELECT" | "TRUE_FALSE";

export type AnswerChoice = {
    USID?: string;
    Text: string;
    IsCorrect: boolean;
    /** Why this choice is right or wrong. Shown only after the quiz is submitted and graded. */
    Rationale?: string | null;
};

export type Question = {
    USID?: string;
    QuestionType: QuestionType;
    Text: string;
    /** How many selections make a complete answer, 1-3. The taker is shown this number. */
    CorrectSelections: number;
    Origin?: "AUTHORED" | "AI_PROPOSED";
    Choices: AnswerChoice[];
};

export type Quiz = {
    USID: string;
    PassingScore: number;
    ShuffleAnswers: boolean;
    AllowPartialCredit: boolean;
    QuestionCount: number;
    Questions: Question[];
};

export type QuizState = {
    ObjectRevisionUSID: string;
    Status: string;
    /** Whether the revision is still a draft. A released quiz is frozen. */
    Editable: boolean;
    TrainingRequired: boolean;
    TrainingRequireAcknowledgment: boolean;
    TrainingRequireRetraining: boolean;
    TrainingRequireQuiz: boolean;
    Quiz: Quiz | null;
};

export async function getQuiz(sessionContext: any, objectRevisionUSID: string): Promise<QuizState> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.get("t1/v1/quizzes", {params: {ObjectRevisionUSID: objectRevisionUSID}});
    return response.data;
}

/**
 * Create or replace the quiz.
 *
 * QUESTIONS OMITTED MEANS "SETTINGS ONLY" -- which is what the Quiz Settings dialog sends. Passing an
 * empty array instead would delete every question somebody wrote, so the key is left out rather than
 * sent as null.
 */
export async function saveQuiz(sessionContext: any, objectRevisionUSID: string, changes: {
    Questions?: Question[];
    PassingScore?: number;
    ShuffleAnswers?: boolean;
    AllowPartialCredit?: boolean;
}): Promise<{Quiz: Quiz | null}> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.put("t1/v1/quizzes", {ObjectRevisionUSID: objectRevisionUSID, ...changes});
    return response.data;
}

export async function deleteQuiz(sessionContext: any, objectRevisionUSID: string): Promise<void> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    await api.delete("t1/v1/quizzes", {params: {ObjectRevisionUSID: objectRevisionUSID}});
}

/**
 * The Training Setup flags, saved as what they are: ordinary fields on the revision.
 *
 * Through the object revision endpoint rather than the quiz one, which is what makes them DRAFT-only,
 * audited and privilege-checked by the machinery that already does that for every other field.
 */
export async function saveTrainingSetup(sessionContext: any, objectRevisionUSID: string, flags: {
    TrainingRequired?: boolean;
    TrainingRequireAcknowledgment?: boolean;
    TrainingRequireRetraining?: boolean;
    TrainingRequireQuiz?: boolean;
}): Promise<void> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    await api.put("t1/v1/object_revisions",
                  {PlatformMessage: {"-idoc": "v1.ObjectRevision-001",
                                     ObjectRevision: {USID: objectRevisionUSID, ...flags}}},
                  {headers: {"idoc-request": "v1.ObjectRevision-001",
                             "idoc-response": "v1.ObjectRevision-001"}});
}

export type Proposal = Question & {Origin: "AI_PROPOSED"};

/**
 * Ask for proposed questions drawn from the record's own text.
 *
 * NOTHING IS SAVED BY THIS. What comes back is a list to review; accepting some of them and calling
 * saveQuiz is what creates rows -- which is why a stored question can honestly say where it started.
 */
export async function proposeQuestions(sessionContext: any, objectRevisionUSID: string, count: number):
        Promise<{Questions: Proposal[]; Model: string | null; SkippedAttachments: string[]}> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.post("t1/v1/quiz_proposals",
                                    {ObjectRevisionUSID: objectRevisionUSID, Count: count});
    return response.data;
}
